<template>
  <Teleport to="body">
    <transition name="confirm-fade">
      <div v-if="open" class="confirm-modal-overlay" @click.self="cancel">
        <div class="confirm-modal-card" role="dialog" aria-modal="true">
          <div class="confirm-modal__icon" :class="`confirm-modal__icon--${tone}`" aria-hidden="true">
            <svg v-if="tone === 'danger'" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 4 L21 19.2 L3 19.2 Z" />
              <path d="M12 10 L12 14.4" />
              <circle cx="12" cy="16.6" r="0.7" fill="currentColor" stroke="none" />
            </svg>
            <svg v-else viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="9.5" />
              <path d="M12 11 L12 16.5" />
              <circle cx="12" cy="7.8" r="0.7" fill="currentColor" stroke="none" />
            </svg>
          </div>
          <h3 class="confirm-modal__title">{{ title }}</h3>
          <p v-if="description" class="confirm-modal__description">{{ description }}</p>
          <div class="confirm-modal__actions">
            <button type="button" class="confirm-modal__btn confirm-modal__btn--ghost" @click="cancel">{{ cancelLabel }}</button>
            <button
              type="button"
              :class="['confirm-modal__btn', tone === 'danger' ? 'confirm-modal__btn--danger' : 'confirm-modal__btn--primary']"
              @click="confirm"
            >{{ confirmLabel }}</button>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script lang="ts">
import { defineComponent, onMounted, onUnmounted, type PropType } from 'vue'

export default defineComponent({
  name: 'ConfirmModal',
  props: {
    open: { type: Boolean, default: false },
    title: { type: String, required: true },
    description: { type: String, default: '' },
    confirmLabel: { type: String, default: 'Confirm' },
    cancelLabel: { type: String, default: 'Cancel' },
    tone: { type: String as PropType<'info' | 'danger'>, default: 'info' },
  },
  emits: ['confirm', 'cancel'],
  setup(props, { emit }) {
    const confirm = () => emit('confirm')
    const cancel = () => emit('cancel')

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && props.open) {
        e.preventDefault()
        cancel()
      }
    }
    onMounted(() => window.addEventListener('keydown', onKey))
    onUnmounted(() => window.removeEventListener('keydown', onKey))

    return { confirm, cancel }
  },
})
</script>

<style scoped>
.confirm-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 300;
  background: rgba(10, 13, 25, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  font-family: var(--font-sans);
}

.confirm-modal-card {
  width: 100%;
  max-width: 420px;
  background: #1a2332;
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  padding: 28px 24px 22px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.45);
  box-sizing: border-box;
  text-align: center;
}

.confirm-modal__icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  margin: 0 auto 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.confirm-modal__icon--info {
  background: rgba(80, 229, 255, 0.18);
  color: #50e5ff;
}
.confirm-modal__icon--danger {
  background: rgba(246, 200, 74, 0.18);
  color: #f6c84a;
}

.confirm-modal__title {
  margin: 0 0 8px;
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
}
.confirm-modal__description {
  margin: 0 0 18px;
  font-size: 13px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.55;
}

.confirm-modal__actions {
  display: flex;
  gap: 8px;
  justify-content: center;
}
.confirm-modal__btn {
  padding: 9px 20px;
  border-radius: 10px;
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background 0.12s ease;
  min-width: 90px;
}
.confirm-modal__btn--ghost {
  background: transparent;
  color: rgba(255, 255, 255, 0.85);
  border-color: rgba(255, 255, 255, 0.15);
}
.confirm-modal__btn--ghost:hover { background: rgba(255, 255, 255, 0.06); }

.confirm-modal__btn--primary {
  background: #96ffc9;
  color: #181b2f;
}
.confirm-modal__btn--primary:hover { background: #82e8b0; }

.confirm-modal__btn--danger {
  background: #fa9696;
  color: #181b2f;
}
.confirm-modal__btn--danger:hover { background: #ff7d7d; }

.confirm-fade-enter-active,
.confirm-fade-leave-active {
  transition: opacity 0.15s ease;
}
.confirm-fade-enter-active .confirm-modal-card,
.confirm-fade-leave-active .confirm-modal-card {
  transition: transform 0.15s ease;
}
.confirm-fade-enter-from,
.confirm-fade-leave-to {
  opacity: 0;
}
.confirm-fade-enter-from .confirm-modal-card,
.confirm-fade-leave-to .confirm-modal-card {
  transform: translateY(8px) scale(0.98);
}
</style>
